import { useMutation, useQueryClient } from "@tanstack/react-query";
import { expenseService } from "./expense.services";
import { expenseKeys } from "./expense.keys";
import { CreateExpenseDto, UpdateExpenseDto } from "./expense.types";
import { message } from "antd";

export const useCreateExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateExpenseDto) => expenseService.createExpense(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expenseKeys.stats() });
      message.success("Expense created successfully!");
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || "Failed to create expense");
    },
  });
};

export const useUpdateExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId, payload }: { expenseId: string; payload: UpdateExpenseDto }) =>
      expenseService.updateExpense({ expenseId, payload }),
    onSuccess: (_, { expenseId }) => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expenseKeys.detail(expenseId) });
      queryClient.invalidateQueries({ queryKey: expenseKeys.stats() });
      message.success("Expense updated successfully!");
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || "Failed to update expense");
    },
  });
};

export const useDeleteExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (expenseId: string) => expenseService.deleteExpense(expenseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expenseKeys.stats() });
      message.success("Expense deleted successfully!");
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || "Failed to delete expense");
    },
  });
};

export const useApproveExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (expenseId: string) => expenseService.approveExpense(expenseId),
    onSuccess: (_, expenseId) => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expenseKeys.detail(expenseId) });
      queryClient.invalidateQueries({ queryKey: expenseKeys.stats() });
      message.success("Expense approved successfully!");
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || "Failed to approve expense");
    },
  });
};

export const useRejectExpense = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ expenseId, notes }: { expenseId: string; notes?: string }) =>
      expenseService.rejectExpense(expenseId, notes),
    onSuccess: (_, { expenseId }) => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expenseKeys.detail(expenseId) });
      queryClient.invalidateQueries({ queryKey: expenseKeys.stats() });
      message.success("Expense rejected successfully!");
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || "Failed to reject expense");
    },
  });
};

export const useMarkExpenseAsPaid = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (expenseId: string) => expenseService.markExpenseAsPaid(expenseId),
    onSuccess: (_, expenseId) => {
      queryClient.invalidateQueries({ queryKey: expenseKeys.lists() });
      queryClient.invalidateQueries({ queryKey: expenseKeys.detail(expenseId) });
      queryClient.invalidateQueries({ queryKey: expenseKeys.stats() });
      message.success("Expense marked as paid successfully!");
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || "Failed to mark expense as paid");
    },
  });
};

export const useCreateExpenseInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: expenseService.createExpenseInvoice,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      queryClient.invalidateQueries({ queryKey: ["expense-stats"] });
    },
  });
}; 